import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import _ from 'lodash';
import './SmartVideo.scss';
import { isSnap } from '../../Helpers/helpers';

// Detect react-snap prerender
const isPrerender = isSnap();

/*
  SmartVideo: Reusable video component with robust autoplay handling, lazy loading,
  and simple fade-in animations (mirrors SmartImage patterns).

  Client-only: during SSR/prerender it renders a placeholder,
  then swaps to the real <video> after mount.

  Example:
  <SmartVideo
    className="hero-video"
    src={videoUrl}
    type="video/mp4"
    autoPlay
    loop
    muted
    playsInline
    preload="auto"
    lazy={false}
    lazyRootMargin="100%"
    onLoadedChange={(loaded) => setLoaded(loaded)}
    controls={false}
    poster={posterUrl} // (optional) shown in prerender placeholder if provided
  />
*/
const SmartVideo = props => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(!props.lazy);
  const [mounted, setMounted] = useState(false);
  const videoRef = useRef(null);

  const rootMargin = props.lazyRootMargin || '100%';

  // Mark as mounted so we can swap from placeholder -> real <video>
  useEffect(() => {
    setMounted(true);
  }, []);

  const isServer = typeof window === 'undefined';
  const showPlaceholder = isServer || isPrerender || !mounted;

  // Notify parent when loaded state changes
  useEffect(() => {
    if (typeof props.onLoadedChange === 'function') {
      props.onLoadedChange(isLoaded);
    }
  }, [isLoaded]);

  // Lazy loading via IntersectionObserver (client only, after mount)
  useEffect(() => {
    if (showPlaceholder || !props.lazy) return;
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      // If no IO support, just load immediately on client
      setShouldLoad(true);
      return;
    }
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );
    if (videoRef.current) observer.observe(videoRef.current);
    return () => observer.disconnect();
  }, [props.lazy, rootMargin, showPlaceholder]);

  // Reset on source change
  useEffect(() => {
    setIsLoaded(false);
  }, [props.src, props.sources]);

  // Handle muted
  useEffect(() => {
    if (!videoRef.current) return;
    // bug in React 16 doesn't allow using these properties directly
    videoRef.current.defaultMuted = props.muted !== false;
    videoRef.current.muted = props.muted !== false;
  }, [mounted, props.muted]);

  const tryPlay = () => {
    if (!props.autoPlay) return;
    const el = videoRef.current;
    if (!el) return;
    const p = el.play();
    if (p && typeof p.then === 'function') {
      p.catch(() => {
        // Ignore autoplay rejections silently
      });
    }
  };

  const handleCanPlay = e => {
    setIsLoaded(true);
    tryPlay();
    if (typeof props.onCanPlay === 'function') props.onCanPlay(e);
  };

  const handleLoadedData = e => {
    setIsLoaded(true);
    tryPlay();
    if (typeof props.onLoadedData === 'function') props.onLoadedData(e);
  };

  const handleError = e => {
    if (typeof props.onError === 'function') props.onError(e);
  };

  // Omit internal-only props from being applied to the DOM element
  const propsToPassThrough = _.omit(props, [
    'autoPlay',
    'className',
    'lazy',
    'lazyRootMargin',
    'noFadeIn',
    'onLoadedChange',
    'overrideTransition',
    'sources',
    'type'
  ]);

  const additionalClasses = {
    'override-transition': props.overrideTransition,
    'no-fade-in': props.noFadeIn || false,
    loading: !isLoaded,
    loaded: isLoaded
  };

  // Build sources: either explicit sources[] or a single src/type
  const renderSources = () => {
    if (!shouldLoad) return null;
    if (props.sources && Array.isArray(props.sources) && props.sources.length > 0) {
      return props.sources.map((source, idx) => (
        <source key={`${source.src || idx}-${source.type || 'video'}`} src={source.src} type={source.type} />
      ));
    }
    if (props.src) {
      return <source src={props.src} type={props.type || 'video/mp4'} />;
    }
    return null;
  };

  // --- PRERENDER / SSR PLACEHOLDER ---
  if (showPlaceholder) {
    // Keep layout stable and SEO-friendly snapshot for react-snap.
    // If a poster is provided, show it; otherwise render an empty box with classes.
    return (
      <div className={cn('smart-video', 'client-only-placeholder', 'loading', props.className)} aria-hidden='true'>
        {props.poster ? (
          <img src={props.poster} alt='' role='presentation' className='smart-video-poster' />
        ) : props.fallbackText ? (
          <span>{props.fallbackText}</span>
        ) : null}
      </div>
    );
  }

  // --- REAL VIDEO (CLIENT AFTER MOUNT) ---
  return (
    <video
      ref={videoRef}
      {...propsToPassThrough}
      className={cn('smart-video', additionalClasses, props.className)}
      autoPlay={!!props.autoPlay}
      loop={props.loop !== false}
      playsInline={props.playsInline !== false}
      preload={props.preload || 'auto'}
      onCanPlay={handleCanPlay}
      onLoadedData={handleLoadedData}
      onError={handleError}
    >
      {renderSources()}
      {props.fallbackText ? <track kind='descriptions' label={props.fallbackText} /> : null}
    </video>
  );
};

SmartVideo.propTypes = {
  // General
  className: PropTypes.string,
  controls: PropTypes.bool,

  // Sources
  src: PropTypes.string,
  type: PropTypes.string,
  sources: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string.isRequired,
      type: PropTypes.string
    })
  ),

  // Playback
  autoPlay: PropTypes.bool,
  muted: PropTypes.bool,
  loop: PropTypes.bool,
  playsInline: PropTypes.bool,
  preload: PropTypes.oneOf(['auto', 'metadata', 'none']),
  poster: PropTypes.string,

  // Behavior
  lazy: PropTypes.bool,
  lazyRootMargin: PropTypes.string,
  onLoadedChange: PropTypes.func,

  // Styling behaviors
  noFadeIn: PropTypes.bool,
  overrideTransition: PropTypes.bool,

  // Events
  onCanPlay: PropTypes.func,
  onLoadedData: PropTypes.func,
  onError: PropTypes.func,
  onPlay: PropTypes.func,
  onPause: PropTypes.func,

  // Optional text shown in prerender placeholder (and as <track> label)
  fallbackText: PropTypes.string
};

SmartVideo.defaultProps = {
  autoPlay: true,
  muted: true,
  loop: true,
  playsInline: true,
  preload: 'auto',
  lazy: false
};

export default SmartVideo;
